import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface RankingItem {
  id_musica: number;
  nome_musica: string;
  id_banda: number;
  nome_banda: string;
  imagem: string | null;
  total_plays: number;
  posicao_atual: number;
  posicao_ontem: number | null;
  posicao_semana: number | null;
  variacao_dia: number;
  variacao_semana: number;
  nova_entrada: boolean;
}

interface Estatisticas {
  periodo_dias: number;
  total_plays: number;
  musicas_tocadas: number;
  ouvintes_unicos: number;
  usuarios_unicos: number;
  bandas_ouvindo: number;
  visitantes_anonimos: number;
  plays_hoje: number;
  plays_ontem: number;
  variacao_diaria_percent: string;
}

interface Movimentacoes {
  maiores_subidas: any[];
  maiores_quedas: any[];
  novas_entradas: any[];
}

interface Alerta {
  tipo: string;
  icone: string;
  titulo: string;
  mensagem: string;
  prioridade: number;
}

interface BandaRanking {
  id_banda: number;
  nome_banda: string;
  imagem: string | null;
  musicas_no_top50: number;
  melhor_posicao: number;
  posicoes: number[];
}

interface PlataformaData {
  plataforma: string;
  total_plays: number;
  percentual: number;
}

const COLORS = ['#ff6600', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
const API_BASE = '/play-analytics';

// Formata numeros grandes com separador de milhar PT-BR (12345 -> "12.345")
const fmt = (n: number | string | null | undefined): string => {
  const num = typeof n === 'string' ? parseInt(n, 10) : n;
  if (num === null || num === undefined || Number.isNaN(num)) return '0';
  return num.toLocaleString('pt-BR');
};

const PlayAnalytics: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<number>(7);

  // Estados dos dados
  const [ranking, setRanking] = useState<RankingItem[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [movimentacoes, setMovimentacoes] = useState<Movimentacoes | null>(null);
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [playsPorHora, setPlaysPorHora] = useState<any[]>([]);
  const [playsPorDia, setPlaysPorDia] = useState<any[]>([]);
  const [plataformas, setPlataformas] = useState<PlataformaData[]>([]);
  const [bandasRanking, setBandasRanking] = useState<BandaRanking[]>([]);
  const [batendoNaPorta, setBatendoNaPorta] = useState<any[]>([]);

  // Estado para música selecionada (histórico)
  const [musicaSelecionada, setMusicaSelecionada] = useState<number | null>(null);
  const [historicoMusica, setHistoricoMusica] = useState<any>(null);

  // Alertas: mostra so os primeiros por padrao (evita encher a tela quando ha muitos)
  const [mostrarTodosAlertas, setMostrarTodosAlertas] = useState<boolean>(false);
  const MAX_ALERTAS_VISIVEIS = 4;

  // Dados que NAO dependem do periodo: carregam uma vez no mount.
  useEffect(() => {
    fetchStaticData();
  }, []);

  // Dados que dependem do periodo (1/7/30 dias): recarregam so o necessario ao trocar.
  useEffect(() => {
    fetchPeriodData();
  }, [periodo]);

  const fetchAllData = async () => {
    await Promise.all([fetchStaticData(), fetchPeriodData()]);
  };

  const fetchStaticData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchRanking(),
        fetchMovimentacoes(),
        fetchAlertas(),
        fetchPlaysPorHora(),
        fetchPlataformas(),
        fetchBandasRanking(),
        fetchBatendoNaPorta()
      ]);
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      setError(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodData = async () => {
    setError(null);
    try {
      await Promise.all([
        fetchEstatisticas(),
        fetchPlaysPorDia()
      ]);
    } catch (error: any) {
      console.error('Erro ao carregar dados do periodo:', error);
      setError(error.message || 'Erro ao carregar dados');
    }
  };

  const fetchRanking = async () => {
    const response = await api.get(`${API_BASE}/ranking`);
    setRanking(response.data.data || []);
  };

  const fetchEstatisticas = async () => {
    const response = await api.get(`${API_BASE}/estatisticas?periodo=${periodo}`);
    setEstatisticas(response.data.data);
  };

  const fetchMovimentacoes = async () => {
    const response = await api.get(`${API_BASE}/movimentacoes`);
    setMovimentacoes(response.data.data);
  };

  const fetchAlertas = async () => {
    const response = await api.get(`${API_BASE}/alertas`);
    setAlertas(response.data.data || []);
  };

  const fetchPlaysPorHora = async () => {
    const response = await api.get(`${API_BASE}/plays-por-hora`);
    const data = response.data.data || [];
    setPlaysPorHora(data.map((item: any) => ({
      ...item,
      hora: format(new Date(item.hora), 'HH:mm', { locale: ptBR })
    })));
  };

  const fetchPlaysPorDia = async () => {
    const response = await api.get(`${API_BASE}/plays-por-dia?periodo=${periodo}`);
    const data = response.data.data || [];
    setPlaysPorDia(data.map((item: any) => ({
      ...item,
      dia: format(new Date(item.dia), 'dd/MM', { locale: ptBR })
    })));
  };

  const fetchPlataformas = async () => {
    const response = await api.get(`${API_BASE}/plataformas`);
    setPlataformas(response.data.data || []);
  };

  const fetchBandasRanking = async () => {
    const response = await api.get(`${API_BASE}/bandas-ranking`);
    setBandasRanking(response.data.data || []);
  };

  const fetchBatendoNaPorta = async () => {
    const response = await api.get(`${API_BASE}/batendo-na-porta`);
    setBatendoNaPorta(response.data.data || []);
  };

  const fetchHistoricoMusica = async (id_musica: number) => {
    try {
      const response = await api.get(`${API_BASE}/historico/${id_musica}`);
      setHistoricoMusica(response.data.data);
      setMusicaSelecionada(id_musica);
    } catch (error) {
      console.error('Erro ao buscar histórico:', error);
    }
  };

  const getVariacaoIcon = (variacao: number, novaEntrada: boolean) => {
    if (novaEntrada) return <span style={styles.novaEntrada}>NOVO</span>;
    if (variacao > 0) return <span style={styles.subiu}>+{variacao}</span>;
    if (variacao < 0) return <span style={styles.desceu}>{variacao}</span>;
    return <span style={styles.estavel}>-</span>;
  };

  const getPosicaoStyle = (posicao: number) => {
    if (posicao === 1) return styles.posicaoOuro;
    if (posicao === 2) return styles.posicaoPrata;
    if (posicao === 3) return styles.posicaoBronze;
    return styles.posicaoNormal;
  };

  if (loading && !estatisticas) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Carregando dados...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <h2>Erro ao carregar Analytics</h2>
          <p>{error}</p>
          <button onClick={fetchAllData} style={styles.retryButton}>Tentar Novamente</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Ranking Top 50 - Analytics</h1>
          <p style={styles.headerSubtitle}>
            Painel do ranking do Palco. O ranking e as movimentacoes sao acumulados (todo o historico);
            os cartoes de metricas e o grafico "Plays por Dia" seguem o periodo selecionado.
          </p>
        </div>
        <div style={styles.periodWrapper}>
          <span style={styles.periodCaption}>Periodo das metricas:</span>
          <div style={styles.periodSelector}>
            <button
              style={periodo === 1 ? styles.periodButtonActive : styles.periodButton}
              onClick={() => setPeriodo(1)}
            >
              1 dia
            </button>
            <button
              style={periodo === 7 ? styles.periodButtonActive : styles.periodButton}
              onClick={() => setPeriodo(7)}
            >
              7 dias
            </button>
            <button
              style={periodo === 30 ? styles.periodButtonActive : styles.periodButton}
              onClick={() => setPeriodo(30)}
            >
              30 dias
            </button>
          </div>
        </div>
      </div>

      {/* KPIs - Visao geral */}
      {estatisticas && (
        <div style={styles.kpiGrid}>
          <div style={styles.kpiCard} title="Total de plays registrados hoje (desde a meia-noite).">
            <div style={styles.kpiLabel}>Plays Hoje</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.plays_hoje)}</div>
            <div style={{
              ...styles.kpiVariacao,
              color: parseFloat(estatisticas.variacao_diaria_percent) >= 0 ? '#4CAF50' : '#f44336'
            }}>
              {parseFloat(estatisticas.variacao_diaria_percent) >= 0 ? '+' : ''}{estatisticas.variacao_diaria_percent}% vs ontem
            </div>
          </div>
          <div style={styles.kpiCard} title={`Soma de todos os plays nos ultimos ${periodo} dias.`}>
            <div style={styles.kpiLabel}>Total de Plays ({periodo}d)</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.total_plays)}</div>
          </div>
          <div style={styles.kpiCard} title={`Quantas musicas diferentes receberam ao menos 1 play nos ultimos ${periodo} dias.`}>
            <div style={styles.kpiLabel}>Musicas Tocadas</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.musicas_tocadas)}</div>
          </div>
          <div style={styles.kpiCard} title="Total de ouvintes distintos (logados + bandas/empresas + visitantes anonimos) no periodo. Inclui os dois cartoes ao lado.">
            <div style={styles.kpiLabel}>Ouvintes Unicos (total)</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.ouvintes_unicos)}</div>
          </div>
          <div style={styles.kpiCard} title="Ouvintes com conta logada (subconjunto dos Ouvintes Unicos).">
            <div style={styles.kpiLabel}>Usuarios Logados</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.usuarios_unicos)}</div>
          </div>
          <div style={styles.kpiCard} title="Ouvintes sem login, identificados por IP (subconjunto dos Ouvintes Unicos).">
            <div style={styles.kpiLabel}>Visitantes Anonimos</div>
            <div style={styles.kpiValue}>{fmt(estatisticas.visitantes_anonimos)}</div>
          </div>
        </div>
      )}

      {/* Alertas de mudancas (compacto + colapsavel) */}
      {alertas.length > 0 && (
        <div style={styles.alertasSection}>
          <div style={styles.alertasHeader}>
            <h3 style={styles.alertasTitulo}>Alertas de mudancas</h3>
            <span style={styles.alertasSubtitulo}>
              Destaques desde ontem: novo #1, entradas no Top 10 e novas entradas no Top 50.
            </span>
          </div>
          <div style={styles.alertasChips}>
            {(mostrarTodosAlertas ? alertas : alertas.slice(0, MAX_ALERTAS_VISIVEIS)).map((alerta, index) => (
              <div key={index} style={styles.alertaChip} title={alerta.mensagem}>
                <span style={styles.alertaIcone}>{alerta.icone}</span>
                <span style={styles.alertaChipTexto}>{alerta.mensagem}</span>
              </div>
            ))}
          </div>
          {alertas.length > MAX_ALERTAS_VISIVEIS && (
            <button
              style={styles.alertasToggle}
              onClick={() => setMostrarTodosAlertas(v => !v)}
            >
              {mostrarTodosAlertas
                ? 'Mostrar menos'
                : `+${alertas.length - MAX_ALERTAS_VISIVEIS} outros alertas`}
            </button>
          )}
        </div>
      )}

      {/* Movimentacoes */}
      {movimentacoes && (
        <div style={styles.movimentacoesGrid}>
          <div style={styles.movimentacaoCard}>
            <h3 style={styles.movimentacaoTitulo}>Maiores Subidas</h3>
            {movimentacoes.maiores_subidas.length > 0 ? (
              movimentacoes.maiores_subidas.map((item, index) => (
                <div key={index} style={styles.movimentacaoItem}>
                  <span style={styles.subiu}>+{item.variacao}</span>
                  <span style={styles.movimentacaoMusica}>{item.nome_musica}</span>
                  <span style={styles.movimentacaoBanda}>{item.nome_banda}</span>
                </div>
              ))
            ) : (
              <p style={styles.semDados}>Nenhuma subida significativa</p>
            )}
          </div>
          <div style={styles.movimentacaoCard}>
            <h3 style={styles.movimentacaoTitulo}>Maiores Quedas</h3>
            {movimentacoes.maiores_quedas.length > 0 ? (
              movimentacoes.maiores_quedas.map((item, index) => (
                <div key={index} style={styles.movimentacaoItem}>
                  <span style={styles.desceu}>{item.variacao}</span>
                  <span style={styles.movimentacaoMusica}>{item.nome_musica}</span>
                  <span style={styles.movimentacaoBanda}>{item.nome_banda}</span>
                </div>
              ))
            ) : (
              <p style={styles.semDados}>Nenhuma queda significativa</p>
            )}
          </div>
          <div style={styles.movimentacaoCard}>
            <h3 style={styles.movimentacaoTitulo}>Novas Entradas</h3>
            {movimentacoes.novas_entradas.length > 0 ? (
              movimentacoes.novas_entradas.map((item, index) => (
                <div key={index} style={styles.movimentacaoItem}>
                  <span style={styles.novaEntrada}>#{item.posicao_atual}</span>
                  <span style={styles.movimentacaoMusica}>{item.nome_musica}</span>
                  <span style={styles.movimentacaoBanda}>{item.nome_banda}</span>
                </div>
              ))
            ) : (
              <p style={styles.semDados}>Nenhuma nova entrada</p>
            )}
          </div>
        </div>
      )}

      {/* Graficos */}
      <div style={styles.chartsGrid}>
        {/* Plays por Hora */}
        <div style={styles.chartSection}>
          <h3 style={styles.sectionTitle}>Plays por Hora (24h)</h3>
          {playsPorHora.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={playsPorHora}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hora" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total_plays" fill="#ff6600" name="Plays" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.chartVazio}>Sem plays nas ultimas 24h</div>
          )}
        </div>

        {/* Plays por Dia */}
        <div style={styles.chartSection}>
          <h3 style={styles.sectionTitle}>Plays por Dia ({periodo}d)</h3>
          {playsPorDia.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={playsPorDia}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total_plays" stroke="#ff6600" name="Plays" />
                <Line type="monotone" dataKey="ouvintes_unicos" stroke="#00C49F" name="Ouvintes" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div style={styles.chartVazio}>Sem plays nos ultimos {periodo} dias</div>
          )}
        </div>
      </div>

      {/* Plataformas */}
      <div style={styles.twoColumnGrid}>
        <div style={styles.chartSection}>
          <h3 style={styles.sectionTitle}>Distribuicao por Plataforma</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={plataformas}
                dataKey="total_plays"
                nameKey="plataforma"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ plataforma, percentual }) => `${plataforma}: ${percentual}%`}
              >
                {plataformas.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bandas no Ranking */}
        <div style={styles.chartSection}>
          <h3 style={styles.sectionTitle}>Bandas com Mais Musicas no Top 50</h3>
          <div style={styles.bandasList}>
            {bandasRanking.map((banda, index) => (
              <div key={banda.id_banda} style={styles.bandaItem}>
                <span style={styles.bandaPosicao}>#{index + 1}</span>
                <span style={styles.bandaNome}>{banda.nome_banda}</span>
                <span style={styles.bandaCount}>{banda.musicas_no_top50} musicas</span>
                <span style={styles.bandaMelhor}>Melhor: #{banda.melhor_posicao}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Batendo na Porta */}
      {batendoNaPorta.length > 0 && (
        <div style={styles.chartSection}>
          <h3 style={styles.sectionTitle}>Batendo na Porta (Posicoes 51-60)</h3>
          <p style={styles.sectionSubtitle}>Musicas proximas de entrar no Top 50</p>
          <div style={styles.batendoList}>
            {batendoNaPorta.map((item, index) => (
              <div key={index} style={styles.batendoItem}>
                <span style={styles.batendoPosicao}>#{item.posicao}</span>
                <div style={styles.batendoInfo}>
                  <span style={styles.batendoMusica}>{item.nome_musica}</span>
                  <span style={styles.batendoBanda}>{item.nome_banda}</span>
                </div>
                <div style={styles.batendoStats}>
                  <span>{fmt(item.total_plays)} plays</span>
                  <span style={styles.batendoFaltando}>Faltam {fmt(item.plays_faltando)} plays</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabela do Ranking */}
      <div style={styles.tableSection}>
        <h3 style={styles.sectionTitle}>Ranking Completo - Top 50</h3>
        <p style={styles.sectionSubtitle}>
          Posicao por total de plays acumulado (todo o historico). As colunas de variacao comparam com o
          snapshot de ontem / de 7 dias atras &mdash; ficam zeradas ou marcadas como "NOVO" enquanto nao
          houver snapshots diarios suficientes acumulados.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>#</th>
              <th style={styles.th}>Var. Dia</th>
              <th style={styles.th}>Var. Semana</th>
              <th style={styles.th}>Musica</th>
              <th style={styles.th}>Banda</th>
              <th style={styles.th}>Plays</th>
              <th style={styles.th}>Acoes</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((item, index) => (
              <tr key={item.id_musica} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                <td style={{ ...styles.td, ...getPosicaoStyle(item.posicao_atual) }}>
                  {item.posicao_atual}
                </td>
                <td style={styles.td}>
                  {getVariacaoIcon(item.variacao_dia, item.nova_entrada)}
                </td>
                <td style={styles.td}>
                  {!item.nova_entrada && getVariacaoIcon(item.variacao_semana, false)}
                </td>
                <td style={styles.td}>
                  <div style={styles.musicaCell}>
                    {item.imagem ? (
                      <img src={item.imagem} alt="" style={styles.musicaThumb} />
                    ) : (
                      <div style={styles.musicaThumbPlaceholder} />
                    )}
                    <strong>{item.nome_musica}</strong>
                  </div>
                </td>
                <td style={styles.td}>{item.nome_banda}</td>
                <td style={styles.td}>{fmt(item.total_plays)}</td>
                <td style={styles.td}>
                  <button
                    onClick={() => fetchHistoricoMusica(item.id_musica)}
                    style={styles.historicoButton}
                  >
                    Historico
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de Historico */}
      {musicaSelecionada && historicoMusica && (
        <div style={styles.modalOverlay} onClick={() => setMusicaSelecionada(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setMusicaSelecionada(null)}>X</button>
            <h3>Historico: {historicoMusica.musica?.nome_musica}</h3>
            <p>{historicoMusica.musica?.nome_banda}</p>
            <div style={styles.historicoList}>
              {historicoMusica.historico?.map((item: any, index: number) => (
                <div key={index} style={styles.historicoItem}>
                  <span>{format(new Date(item.data), 'dd/MM/yyyy', { locale: ptBR })}</span>
                  <span>Posicao: #{item.posicao}</span>
                  <span>{fmt(item.total_plays)} plays</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '20px',
  } as React.CSSProperties,
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
  } as React.CSSProperties,
  headerSubtitle: {
    fontSize: '13px',
    color: '#666',
    margin: '6px 0 0 0',
    maxWidth: '620px',
    lineHeight: 1.4,
  } as React.CSSProperties,
  periodWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '6px',
  } as React.CSSProperties,
  periodCaption: {
    fontSize: '11px',
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  } as React.CSSProperties,
  periodSelector: {
    display: 'flex',
    gap: '10px',
  } as React.CSSProperties,
  periodButton: {
    padding: '10px 20px',
    border: '1px solid #ddd',
    backgroundColor: 'white',
    cursor: 'pointer',
    borderRadius: '5px',
    fontSize: '14px',
  } as React.CSSProperties,
  periodButtonActive: {
    padding: '10px 20px',
    border: '1px solid #ff6600',
    backgroundColor: '#ff6600',
    color: 'white',
    cursor: 'pointer',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
  } as React.CSSProperties,
  alertasSection: {
    backgroundColor: '#fff8f2',
    padding: '14px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    border: '1px solid #ffe0c2',
  } as React.CSSProperties,
  alertasHeader: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '10px',
  } as React.CSSProperties,
  alertasTitulo: {
    margin: 0,
    fontSize: '15px',
    color: '#e65100',
  } as React.CSSProperties,
  alertasSubtitulo: {
    fontSize: '12px',
    color: '#999',
  } as React.CSSProperties,
  alertasChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  } as React.CSSProperties,
  alertaChip: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: 'white',
    border: '1px solid #ffe0c2',
    borderRadius: '16px',
    fontSize: '12px',
    maxWidth: '340px',
  } as React.CSSProperties,
  alertaChipTexto: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#444',
  } as React.CSSProperties,
  alertasToggle: {
    marginTop: '10px',
    padding: '4px 10px',
    background: 'none',
    border: '1px solid #ff6600',
    color: '#ff6600',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  } as React.CSSProperties,
  alertaIcone: {
    fontSize: '16px',
  } as React.CSSProperties,
  chartVazio: {
    height: '250px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    fontSize: '14px',
  } as React.CSSProperties,
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  } as React.CSSProperties,
  kpiCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    textAlign: 'center',
  } as React.CSSProperties,
  kpiLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '8px',
  } as React.CSSProperties,
  kpiValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
  } as React.CSSProperties,
  kpiVariacao: {
    fontSize: '11px',
    marginTop: '5px',
  } as React.CSSProperties,
  movimentacoesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  } as React.CSSProperties,
  movimentacaoCard: {
    backgroundColor: 'white',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  movimentacaoTitulo: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    color: '#333',
    borderBottom: '1px solid #eee',
    paddingBottom: '8px',
  } as React.CSSProperties,
  movimentacaoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 0',
    borderBottom: '1px solid #f5f5f5',
  } as React.CSSProperties,
  movimentacaoMusica: {
    flex: 1,
    fontSize: '13px',
    fontWeight: '500',
  } as React.CSSProperties,
  movimentacaoBanda: {
    fontSize: '11px',
    color: '#666',
  } as React.CSSProperties,
  semDados: {
    color: '#999',
    fontSize: '13px',
    textAlign: 'center',
    padding: '10px',
  } as React.CSSProperties,
  subiu: {
    color: '#4CAF50',
    fontWeight: 'bold',
    fontSize: '13px',
    minWidth: '40px',
  } as React.CSSProperties,
  desceu: {
    color: '#f44336',
    fontWeight: 'bold',
    fontSize: '13px',
    minWidth: '40px',
  } as React.CSSProperties,
  estavel: {
    color: '#999',
    fontSize: '13px',
    minWidth: '40px',
  } as React.CSSProperties,
  novaEntrada: {
    backgroundColor: '#ff6600',
    color: 'white',
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 'bold',
  } as React.CSSProperties,
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  } as React.CSSProperties,
  chartSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px',
    margin: '0 0 15px 0',
  } as React.CSSProperties,
  sectionSubtitle: {
    fontSize: '12px',
    color: '#666',
    marginTop: '-10px',
    marginBottom: '15px',
  } as React.CSSProperties,
  twoColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  } as React.CSSProperties,
  bandasList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  } as React.CSSProperties,
  bandaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px',
    backgroundColor: '#f9f9f9',
    borderRadius: '5px',
  } as React.CSSProperties,
  bandaPosicao: {
    fontWeight: 'bold',
    color: '#ff6600',
    minWidth: '30px',
  } as React.CSSProperties,
  bandaNome: {
    flex: 1,
    fontWeight: '500',
  } as React.CSSProperties,
  bandaCount: {
    fontSize: '12px',
    color: '#666',
  } as React.CSSProperties,
  bandaMelhor: {
    fontSize: '11px',
    color: '#999',
  } as React.CSSProperties,
  batendoList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  } as React.CSSProperties,
  batendoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '10px',
    backgroundColor: '#f9f9f9',
    borderRadius: '5px',
  } as React.CSSProperties,
  batendoPosicao: {
    fontWeight: 'bold',
    color: '#666',
    fontSize: '14px',
    minWidth: '35px',
  } as React.CSSProperties,
  batendoInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,
  batendoMusica: {
    fontWeight: '500',
    fontSize: '14px',
  } as React.CSSProperties,
  batendoBanda: {
    fontSize: '12px',
    color: '#666',
  } as React.CSSProperties,
  batendoStats: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    fontSize: '12px',
  } as React.CSSProperties,
  batendoFaltando: {
    color: '#ff6600',
    fontWeight: '500',
  } as React.CSSProperties,
  tableSection: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  } as React.CSSProperties,
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f9f9f9',
    fontWeight: 'bold',
    color: '#333',
    fontSize: '13px',
  } as React.CSSProperties,
  td: {
    padding: '12px',
    borderBottom: '1px solid #eee',
    fontSize: '13px',
  } as React.CSSProperties,
  musicaCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  } as React.CSSProperties,
  musicaThumb: {
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    objectFit: 'cover',
    flexShrink: 0,
  } as React.CSSProperties,
  musicaThumbPlaceholder: {
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    backgroundColor: '#eee',
    flexShrink: 0,
  } as React.CSSProperties,
  trEven: {
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
  trOdd: {
    backgroundColor: 'white',
  } as React.CSSProperties,
  posicaoOuro: {
    backgroundColor: '#FFD700',
    color: '#333',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '4px',
  } as React.CSSProperties,
  posicaoPrata: {
    backgroundColor: '#C0C0C0',
    color: '#333',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '4px',
  } as React.CSSProperties,
  posicaoBronze: {
    backgroundColor: '#CD7F32',
    color: 'white',
    fontWeight: 'bold',
    textAlign: 'center',
    borderRadius: '4px',
  } as React.CSSProperties,
  posicaoNormal: {
    fontWeight: 'bold',
    textAlign: 'center',
  } as React.CSSProperties,
  historicoButton: {
    padding: '5px 10px',
    backgroundColor: '#ff6600',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
  } as React.CSSProperties,
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  } as React.CSSProperties,
  modal: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'relative',
  } as React.CSSProperties,
  modalClose: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
  } as React.CSSProperties,
  historicoList: {
    marginTop: '15px',
  } as React.CSSProperties,
  historicoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px',
    borderBottom: '1px solid #eee',
    fontSize: '13px',
  } as React.CSSProperties,
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '24px',
    color: '#666',
  } as React.CSSProperties,
  error: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    color: '#d32f2f',
  } as React.CSSProperties,
  retryButton: {
    marginTop: '20px',
    padding: '10px 20px',
    backgroundColor: '#ff6600',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '16px',
  } as React.CSSProperties,
};

export default PlayAnalytics;
